import React, { useEffect } from "react";
import { Icon, Button } from '@blueprintjs/core';
import { fabric } from "fabric";

export function Duplicate(editor) {
  const { disabledObj, Canvaseditor } = editor;

  const moveActiveObject = (dx, dy) => {
    if (!Canvaseditor) return;
    const activeObject = Canvaseditor.getActiveObject();
    if (!activeObject) return;

    const left = (activeObject.left || 0) + dx;
    const top = (activeObject.top || 0) + dy;

    activeObject.set({
      left,
      top,
    });
    activeObject.setCoords();
    Canvaseditor.renderAll();
    if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("canvas:state-changed");
  };

  // === Viewport-level zoom for keyboard shortcuts (Ctrl + '+' / '-' / '0') ===
  // These should scale the whole artboard, not just individual elements.
  const VIEWPORT_ZOOM_STEP = 1.1;
  const MIN_VIEWPORT_ZOOM = 0.3;
  const MAX_VIEWPORT_ZOOM = 3;
  let currentViewportZoom = 1;

  const applyViewportZoom = () => {
    const container = document.querySelector(".main-area .canvas-area");
    if (!container) return;
    container.style.transform = `scale(${currentViewportZoom})`;
    container.style.transformOrigin = "center center";
  };

  const setViewportZoom = (nextZoom) => {
    currentViewportZoom = Math.max(
      MIN_VIEWPORT_ZOOM,
      Math.min(MAX_VIEWPORT_ZOOM, nextZoom || 1)
    );
    applyViewportZoom();
  };

  const zoomInViewport = () => {
    setViewportZoom(currentViewportZoom * VIEWPORT_ZOOM_STEP);
  };

  const zoomOutViewport = () => {
    setViewportZoom(currentViewportZoom / VIEWPORT_ZOOM_STEP);
  };

  const resetViewportZoom = () => {
    setViewportZoom(1);
  };

  const selectAllObjects = () => {
    if (!Canvaseditor) return;
    const objects = Canvaseditor.getObjects();
    if (!objects || objects.length === 0) return;

    const selection = new fabric.ActiveSelection(objects, {
      canvas: Canvaseditor,
    });
    Canvaseditor.setActiveObject(selection);
    Canvaseditor.requestRenderAll();
  };

  // Set dublicate object.
  const handleDuplicate = () => {
    if (Canvaseditor.getActiveObject()) {
      // clone again, so you can do multiple copies.
      Canvaseditor.getActiveObject().clone(function (clonedObj) {
        Canvaseditor.discardActiveObject();
        clonedObj.set({
          left: clonedObj.left + 10,
          top: clonedObj.top + 10,
          evented: true
        });
        if (clonedObj.type === 'activeSelection') {
          // active selection needs a reference to the canvas.
          // clonedObj.canvas = canvas;
          clonedObj.forEachObject(function (obj) {
            Canvaseditor.add(obj);
          });
          // this should solve the unselectability
          clonedObj.setCoords();
        } else {
          Canvaseditor.add(clonedObj);
        }
        Canvaseditor.setActiveObject(clonedObj);
        Canvaseditor.requestRenderAll();
        if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("canvas:state-changed");
      });

      Canvaseditor.renderAll();
    }
  };

  const copySelectedObject = () => {
    if (Canvaseditor) {
      const activeObject = Canvaseditor.getActiveObject();
      Canvaseditor.clipboard = activeObject;
      Canvaseditor.renderAll();
    }
  };

  const pasteCopiedObject = () => {
    if (Canvaseditor.clipboard) {
      Canvaseditor.clipboard.clone((cloned) => {
        Canvaseditor.discardActiveObject();
        cloned.set({
          left: cloned.left + 10,
          top: cloned.top + 10,
          evented: true,
        });
        if (cloned.type === "activeSelection") {
          // active selection needs a reference to the canvas.
          cloned.canvas = Canvaseditor;
          cloned.forEachObject((obj) => {
            Canvaseditor.add(obj);
          });
          cloned.setCoords();
        } else {
          Canvaseditor.add(cloned);
        }
        Canvaseditor.setActiveObject(cloned);
        Canvaseditor.requestRenderAll();
        if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("canvas:state-changed");
      });
      Canvaseditor.renderAll();
    }
  };

  const cutSelectedObject = () => {
    if (Canvaseditor) {
      const activeObject = Canvaseditor.getActiveObject();
      Canvaseditor.clipboard = activeObject;
      Canvaseditor.remove(activeObject);
      Canvaseditor.renderAll();
      if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("canvas:state-changed");
    }
  };

  const removeSelectedObjects = () => {
    if (Canvaseditor) {
      const activeObject = Canvaseditor.getActiveObject();
      Canvaseditor.remove(activeObject);
      Canvaseditor.renderAll();
      if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("canvas:state-changed");
    }
  };

  const handleKeyDown = (e) => {
    if (!Canvaseditor) return;

    const target = e.target || e.srcElement;
    const isEditableTarget =
      target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable);

    if (e.ctrlKey || e.metaKey) {
      // Existing shortcuts: MUST remain unchanged.
      if (e.key === "c") {
        copySelectedObject();
        return;
      } else if (e.key === "v") {
        if (isEditableTarget) {
          // Ignore the paste event for inputs or editable areas
          return;
        }
        pasteCopiedObject();
        return;
      } else if (e.key === "x") {
        cutSelectedObject();
        return;
      }

      // New fine movement shortcuts (1px): Ctrl + Arrow keys
      if (!isEditableTarget) {
        switch (e.key) {
          case "ArrowLeft":
            e.preventDefault();
            moveActiveObject(-1, 0);
            return;
          case "ArrowRight":
            e.preventDefault();
            moveActiveObject(1, 0);
            return;
          case "ArrowUp":
            e.preventDefault();
            moveActiveObject(0, -1);
            return;
          case "ArrowDown":
            e.preventDefault();
            moveActiveObject(0, 1);
            return;
          case "+":
          case "=": // Support Ctrl + '+' (often reported as '=' with Shift)
            e.preventDefault();
            zoomInViewport();
            return;
          case "-":
            e.preventDefault();
            zoomOutViewport();
            return;
          case "0":
            e.preventDefault();
            resetViewportZoom();
            return;
          case "a":
          case "A":
            e.preventDefault();
            selectAllObjects();
            return;
          default:
            break;
        }
      }
    } else {
      // Existing delete shortcut: MUST remain unchanged.
      if (e.key === "Delete") {
        removeSelectedObjects();
        return;
      }

      // New coarse movement shortcuts (5px): Arrow keys without Ctrl/Meta
      if (!isEditableTarget) {
        switch (e.key) {
          case "ArrowLeft":
            e.preventDefault();
            moveActiveObject(-5, 0);
            return;
          case "ArrowRight":
            e.preventDefault();
            moveActiveObject(5, 0);
            return;
          case "ArrowUp":
            e.preventDefault();
            moveActiveObject(0, -5);
            return;
          case "ArrowDown":
            e.preventDefault();
            moveActiveObject(0, 5);
            return;
          default:
            break;
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [Canvaseditor]);

  return (
    <div className="duplicate">
      <Button
        disabled={disabledObj}
        onClick={handleDuplicate}
        icon={<Icon icon="duplicate" />}
      />
    </div>
  );
}
